/**
 * `gamification-core` — CONFIG of the melodimals domain (kid-games).
 *
 * POSITIVE retrofit and without comparisons: the app already rewards with STICKERS (one per
 * mini-game and per song, see `rewards/stickers.ts`). Here we do NOT duplicate that
 * system: we UNIFY it under the agnostic kit by adding XP/levels and badges
 * (milestone medals) that are DERIVED from the same stickers and from the days
 * played. The `RewardPopup` for a new sticker stays as is — the kit lives BELOW.
 *
 * Designed for 2-6 years: nothing punitive, no exposed league/leaderboard,
 * short and friendly streaks. Everything is DERIVED from the local `Progress` (see `derive.ts`).
 */
import { counterAtLeast, streakAtLeast } from "./badges";
import type { GamificationConfig } from "./engine";

/** Domain event types (derived from the persisted local `Progress`). */
export const MELODIMALS_EVENTS = {
  /** A mini-game completed (sticker `game:*`). */
  gameCompleted: "game.completed",
  /** A song learned (sticker `song:*`). */
  songLearned: "song.learned",
  /** Any sticker earned (game or song) — the base reward. */
  stickerEarned: "sticker.earned",
  /** A day on which the child played (feeds the friendly streak). */
  playDay: "play.day",
} as const;

/** Streak line: days with activity (friendly play streak). */
export const PLAY_STREAK_LINE = "play";

export const melodimalsGamificationConfig: GamificationConfig = {
  xp: {
    [MELODIMALS_EVENTS.gameCompleted]: 20,
    [MELODIMALS_EVENTS.songLearned]: 20,
    [MELODIMALS_EVENTS.stickerEarned]: 10,
  },
  streakLines: {
    [MELODIMALS_EVENTS.playDay]: [PLAY_STREAK_LINE],
  },
  counters: {
    [MELODIMALS_EVENTS.gameCompleted]: ["games_played"],
    [MELODIMALS_EVENTS.songLearned]: ["songs"],
    [MELODIMALS_EVENTS.stickerEarned]: ["stickers"],
    [MELODIMALS_EVENTS.playDay]: ["play_days"],
  },
  badges: [
    {
      id: "first-game",
      title: "First game",
      description: "Finish your first mini-game.",
      when: counterAtLeast("games_played", 1),
    },
    {
      id: "all-games",
      title: "All games",
      description: "Master every mini-game.",
      when: counterAtLeast("games_played", 3),
      bonusXp: 20,
    },
    {
      id: "first-song",
      title: "First song",
      description: "Learn your first song.",
      when: counterAtLeast("songs", 1),
    },
    {
      id: "music-lover",
      title: "Music lover",
      description: "Learn five songs.",
      when: counterAtLeast("songs", 5),
      bonusXp: 20,
    },
    {
      id: "play-streak-3",
      title: "Three days playing",
      description: "Play three days in a row.",
      when: streakAtLeast(PLAY_STREAK_LINE, 3),
      bonusXp: 15,
    },
    {
      id: "collector",
      title: "Collector",
      description: "Earn six stickers.",
      when: counterAtLeast("stickers", 6),
    },
    {
      id: "super-collector",
      title: "Super collector",
      description: "Earn ten stickers.",
      when: counterAtLeast("stickers", 10),
      bonusXp: 30,
    },
  ],
};
